import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';

import { useRouteConfiguration } from '../../../context/routeConfigurationContext';
import { FormattedMessage, useIntl } from '../../../util/reactIntl';
import { pathByRouteName } from '../../../util/routes';
import {
  SELLER_DASHBOARD_MAIN_NAV_ITEMS,
  SELLER_SETTINGS_NAV_PARENT,
  SellerNavIconLogout,
} from '../../../util/sellerNavItems';

import NamedLink from '../../../components/NamedLink/NamedLink';
import NotificationBadge from '../../../components/NotificationBadge/NotificationBadge';

import LayoutWrapperAccountSettingsSideNav from './LayoutWrapperAccountSettingsSideNav';

import css from './LayoutSideNavigation.module.css';

/** Account routes rendered under the Settings disclosure (matches `LayoutWrapperAccountSettingsSideNav`). */
const ACCOUNT_SETTINGS_PAGES = new Set([
  'ProfileSettingsPage',
  'ContactDetailsPage',
  'PasswordChangePage',
  'StripePayoutPage',
  'PaymentMethodsPage',
  'ManageAccountPage',
]);

const ChevronDown = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path
      d="M6 9l6 6 6-6"
      stroke="currentColor"
      strokeWidth="1.75"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

/**
 * Seller hub sidebar: main nav links + Settings group with account sub-links.
 * Visual style matches `SellerDashboardPage` sidebar.
 *
 * @param {Object} props
 * @param {Function} props.onLogout — `(historyPush) => void`
 * @param {Object} props.accountSettingsNavProps — passed to embedded account nav
 */
const LayoutSellerSideNav = ({ onLogout, accountSettingsNavProps }) => {
  const intl = useIntl();
  const history = useHistory();
  const location = useLocation();
  const routeConfiguration = useRouteConfiguration();
  const orderNotificationsCount = useSelector(
    state => state.user?.currentUserOrderNotificationCount ?? 0
  );
  const saleNotificationsCount = useSelector(
    state => state.user?.currentUserSaleNotificationCount ?? 0
  );

  const { currentPage } = accountSettingsNavProps;
  const isAccountSection = ACCOUNT_SETTINGS_PAGES.has(currentPage);
  const [settingsMenuOpen, setSettingsMenuOpen] = useState(isAccountSection);

  useEffect(() => {
    if (!isAccountSection) {
      setSettingsMenuOpen(false);
    }
  }, [currentPage, isAccountSection]);

  const settingsExpanded = isAccountSection || settingsMenuOpen;
  const toggleSettingsMenu = () => setSettingsMenuOpen(v => !v);

  const overviewPath = pathByRouteName('SellerDashboardPage', routeConfiguration);
  const isPathActive = (routeName, params = {}) => {
    const p = pathByRouteName(routeName, routeConfiguration, params);
    return location.pathname === p;
  };

  const inboxNotificationCountForItem = ({ routeName, params }) => {
    if (routeName !== 'InboxPage') {
      return 0;
    }
    if (params?.tab === 'orders') {
      return orderNotificationsCount;
    }
    if (params?.tab === 'sales') {
      return saleNotificationsCount;
    }
    return 0;
  };

  const renderMainLink = item => {
    const { routeName, params, messageId, Icon } = item;
    const pathMatches = isPathActive(routeName, params);
    const active =
      routeName === 'SellerDashboardPage'
        ? location.pathname === overviewPath
        : pathMatches;

    const notificationCount = inboxNotificationCountForItem(item);

    return (
      <NamedLink
        key={messageId}
        name={routeName}
        params={params}
        className={classNames(css.sellerNavLink, { [css.sellerNavLinkActive]: active })}
      >
        <span className={css.sellerNavIcon}>
          <Icon />
        </span>
        <span className={css.sellerNavLinkLabel}>
          <FormattedMessage id={messageId} />
        </span>
        {notificationCount > 0 ? (
          <NotificationBadge className={css.sellerNavNotificationBadge} count={notificationCount} />
        ) : null}
      </NamedLink>
    );
  };

  return (
    <div className={css.sellerSideNav}>
      {/* <p className={css.sellerSideNavLogo}>
        <FormattedMessage id="SellerDashboard.logo" />
      </p> */}
      <nav
        className={css.sellerSideNavMain}
        aria-label={intl.formatMessage({ id: 'SellerDashboard.sidebarAria' })}
      >
        {SELLER_DASHBOARD_MAIN_NAV_ITEMS.map(item => renderMainLink(item))}

        <button
          type="button"
          id="seller-hub-settings-trigger"
          className={classNames(css.sellerNavLink, css.sellerNavSettingsTrigger, {
            [css.sellerNavLinkActive]: isAccountSection,
          })}
          aria-expanded={settingsExpanded}
          aria-controls="seller-hub-settings-submenu"
          onClick={toggleSettingsMenu}
        >
          <span className={css.sellerNavIcon}>
            <SELLER_SETTINGS_NAV_PARENT.Icon />
          </span>
          <FormattedMessage id={SELLER_SETTINGS_NAV_PARENT.messageId} />
          <span
            className={classNames(css.sellerNavChevron, {
              [css.sellerNavChevronOpen]: settingsExpanded,
            })}
          >
            <ChevronDown />
          </span>
        </button>
        {settingsExpanded ? (
          <div id="seller-hub-settings-submenu" className={css.sellerNavSubmenu}>
            <LayoutWrapperAccountSettingsSideNav
              accountSettingsNavProps={accountSettingsNavProps}
              ariaLabel={intl.formatMessage({
                id: 'LayoutSideNavigation.screenreader.accountNavigation',
              })}
              variant="embedded"
            />
          </div>
        ) : null}
      </nav>

      <div className={css.sellerSideNavFooter}>
        <button
          type="button"
          className={css.sellerNavLogoutBtn}
          onClick={() => onLogout(history.push)}
        >
          <span className={css.sellerNavIcon}>
            <SellerNavIconLogout />
          </span>
          <FormattedMessage id="SellerDashboard.logout" />
        </button>
      </div>
    </div>
  );
};

LayoutSellerSideNav.propTypes = {
  onLogout: PropTypes.func.isRequired,
  accountSettingsNavProps: PropTypes.shape({
    currentPage: PropTypes.string.isRequired,
    showPaymentMethods: PropTypes.bool,
    showPayoutDetails: PropTypes.bool,
  }).isRequired,
};

export default LayoutSellerSideNav;
